// import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@frontegg/react';

import { VolumeResponse } from '~/shared/types/api';
import { SelectOption } from '~/shared/utils/types';
import { DatafySwitchContext } from '~/shared/context/DatafySwitchProvider';
import Table from '~/shared/components/Table';

import TagsTooltip from '../../../common/TagsTooltip';
import { canBeDatafied } from '../../helpers';
import { apiFilterKey } from '../../../common/serachParamKeys';

import { AllVolumesTableHeader } from './AllVolumesTableHeader';
import { useAppliedFilters } from './hooks/useAppliedFilters';
import { useResponsiveHeaderHeight } from './hooks/useResponsiveHeaderHeight';
import { getColumnDefs } from './columnDefs';

export const DATA_LIMIT = 50;
const sortKey = 'sort_field';
// const headerHeight = '88px';
// const navWidth = '4rem';

function AllVolumesTable({
  setPageOffset,
  pageOffset,
  sortField,
  data,
  loading = false,
}: {
  setPageOffset: React.Dispatch<React.SetStateAction<string>>;
  pageOffset: string;
  sortField: { field: string; dir: 'desc' | 'asc' };
  data:
    | ({ volumes: VolumeResponse['volumes']; count: number } & Partial<VolumeResponse>)
    | undefined;
  loading?: boolean;
}) {
  const [_searchParams, setSearchParams] = useSearchParams();
  const { datafySwitchVolumes } = useContext(DatafySwitchContext);

  const [selectedVolumes, setSelectedVolumes] = useState<string[]>([]);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [dataCount, setDataCount] = useState<number>();

  const { user } = useAuth();
  const isUserReadOnly = !!user?.roles?.find((r) => r.key === 'ReadOnly');

  const {
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAutoscaleState,
    activeFilterCount,
  } = useAppliedFilters();

  const { headerHeight, windowWidth } = useResponsiveHeaderHeight();

  useEffect(() => {
    if (data?.count !== undefined) {
      setDataCount(data.count);
    }
  }, [data?.count]);

  useEffect(() => {
    if (!dataCount) {
      return;
    }
    const totalPages = Math.ceil(dataCount / DATA_LIMIT) || 1;
    const currentPageIdx = Math.floor((parseInt(pageOffset, 10) || 0) / DATA_LIMIT);
    if (currentPageIdx > totalPages - 1) {
      setPageOffset(`${(totalPages - 1) * DATA_LIMIT}`);
    }
  }, [dataCount, pageOffset, setPageOffset]);

  const toggleSidePanel = useCallback(() => setSidePanelOpen((o) => !o), []);
  const setPanelOpen = useCallback((open: boolean) => setSidePanelOpen(open), []);

  const rowData = useMemo(() => data?.volumes, [data?.volumes]);

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        onSelectAllRows: () =>
          setSelectedVolumes((prev) =>
            prev?.length > 0
              ? []
              : data?.volumes.filter(canBeDatafied).map((v) => v.volumeId!) || []
          ),
        sortField,
        selectedVolumes,
        setSelectedVolumes,
        datafySwitchVolumes,
        windowWidth,
        isUserReadOnly,
      }),
    [sortField, datafySwitchVolumes, windowWidth, selectedVolumes, data?.volumes, isUserReadOnly]
  );

  const onPageChange = (page: number): void => {
    setSelectedVolumes([]);
    setPageOffset(`${page * DATA_LIMIT}`);
  };

  const handleResetFilters = () => {
    setPageOffset('0');
    setSearchParams(
      (params) => {
        params.delete(apiFilterKey);
        params.delete('autoscaling_state');
        params.delete('processed_filters');
        return params;
      },
      { replace: true }
    );
    setSidePanelOpen(false);
  };

  return (
    <div className="flex flex-col min-h-[348px] h-full gap-2.5 2xl:gap-3.5 ">
      <AllVolumesTableHeader
        dataCount={loading ? undefined : dataCount}
        activeFilterCount={activeFilterCount}
        sidePanelOpen={sidePanelOpen}
        onTogglePanel={toggleSidePanel}
        onSetPanelOpen={setPanelOpen}
        onResetFilters={handleResetFilters}
        appliedClusterNameFilters={appliedClusterNameFilters as SelectOption[]}
        appliedInstanceFilters={appliedInstanceFilters as SelectOption[]}
        appliedVolumeIdFilters={appliedVolumeIdFilters as SelectOption[]}
        appliedVolumeTagFilters={appliedVolumeTagFilters as SelectOption[]}
        appliedAutoscaleState={appliedAutoscaleState}
      />
      <Table
        rowData={rowData}
        columnDefs={columnDefs}
        headerHeight={headerHeight.height}
        loading={loading}
        loadingVariant="fleetManager"
        disableClientSort
        suppressNoRowsWhileLoading
        itemsName={'volumes'}
        getRowId={(params) => params.data.volumeId!}
        pagination={{
          perPage: DATA_LIMIT,
          onChange: onPageChange,
          totalPages: Math.ceil((dataCount || DATA_LIMIT) / DATA_LIMIT),
          dataCount: dataCount || 0,
          itemsName: 'volumes',
          currentPage: Math.floor((parseInt(pageOffset, 10) || 0) / DATA_LIMIT),
        }}
        onSortChange={(sort) => {
          setSearchParams(
            (params) => {
              params.set(sortKey, JSON.stringify(sort));
              return params;
            },
            { replace: true }
          );
        }}
      />

      {rowData?.map((data) => <TagsTooltip key={data?.volumeId} {...data} />)}
    </div>
  );
}

export default AllVolumesTable;
